import { Context, createContext, useContext, useState } from "react";

type ProgressBarProps = {
  currentStep: number;
};

type MultiStepFormContextType<T> = {
  formData: T;
  updateFormData: (data: Partial<T>) => void;
  resetFormData: (data: Partial<T>) => void;
  currentStep: number;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
};

const MultiStepFormContext = createContext<MultiStepFormContextType<unknown>>({
  formData: {},
  updateFormData: () => {},
  resetFormData: () => {},
  currentStep: 0,
  nextStep: () => {},
  previousStep: () => {},
  goToStep: () => {},
});

export function useMultiStepForm<T>() {
  const context = useContext(
    MultiStepFormContext as unknown as Context<MultiStepFormContextType<T>>
  );
  if (!context) {
    throw new Error("MultiStepFormContext not found");
  }
  return context as MultiStepFormContextType<T>;
}

export function MultiStepForm<T>({
  initialData,
  children,
  onStepChange,
  defaultStep = 0,
  onFinish,
  progressBar: ProgressBar,
}: {
  initialData: T;
  children: React.ReactNode[];
  onStepChange?: (step: number) => void;
  defaultStep?: number;
  onFinish?: (formData: T) => void;
  progressBar?: React.FunctionComponent<ProgressBarProps>;
}) {
  const [formData, setFormData] = useState<T>(initialData);
  const [currentStep, setCurrentStep] = useState(defaultStep);

  const nextStep = () => {
    if (currentStep < children.length - 1) {
      setCurrentStep(currentStep + 1);
      onStepChange?.(currentStep + 1);
    } else {
      onFinish?.(formData);
    }
  };

  const previousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      onStepChange?.(currentStep - 1);
    }
  };

  function updateFormData(data: Partial<T>) {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  }

  function resetFormData(data: Partial<T>) {
    setFormData(data as T);
  }

  const goToStep = (step: number) => {
    setCurrentStep(step);
    onStepChange?.(step);
  };

  return (
    <MultiStepFormContext.Provider
      value={{
        formData,
        updateFormData,
        resetFormData,
        currentStep,
        nextStep,
        previousStep,
        goToStep,
      }}
    >
      {ProgressBar ? <ProgressBar currentStep={currentStep} /> : null}
      {children[currentStep]}
    </MultiStepFormContext.Provider>
  );
}
